import { Component } from '@theme/component';
import Splide from '@splidejs/splide';

/**
 * A custom element that manages a grid carousel with Splide.js.
 *
 * @typedef {object} GridCarouselRefs
 * @property {HTMLElement} [arrows] - The arrows container element.
 *
 * @extends Component<GridCarouselRefs>
 */
class GridCarousel extends Component {
  /** @type {MediaQueryList} */
  #mobileMediaQuery = window.matchMedia('(width < 64rem)');

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {import('@splidejs/splide').Splide} */
  #splide;

  connectedCallback() {
    super.connectedCallback();

    const hasArrows = Boolean(this.refs.arrows);
    const movePerPage = this.hasAttribute('data-move-per-page');

    this.#splide = new Splide(
      this,
      /** @type {import('@splidejs/splide').Options} */
      {
        autoWidth: true,
        arrows: hasArrows,
        padding: {
          left: 'var(--padding-inline-start)',
          right: 'var(--padding-inline-end)',
        },
        gap: 'var(--gap)',
        perMove: movePerPage ? this.#columns : 1,
        perPage: this.#columns,
        omitEnd: true,
        pagination: false,
        mediaQuery: 'max',
        easing: 'cubic-bezier(0.33, 1, 0.68, 1)', // easeOutCubic
        focus: this.#currentFocus,
        type: this.#currentType,
      },
    );

    /** @type {boolean|undefined} */
    let isOverflow = undefined;

    this.#splide.on(
      'overflow',
      function (/** @type {boolean} */ newIsOverflow) {
        if (isOverflow !== newIsOverflow) {
          isOverflow = Boolean(newIsOverflow);

          this.#splide.options = {
            drag: isOverflow,
          };
        }
      }.bind(this),
    );

    this.#mobileMediaQuery.addEventListener(
      'change',
      (e) => {
        this.#splide.options = {
          perMove: this.#columns,
          focus: this.#currentFocus,
          type: this.#currentType,
        };

        this.#splide.destroy();
        this.#splide.mount();
      },
      { signal: this.#abortController.signal },
    );

    this.#splide.mount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#splide?.destroy(true);
  }

  get #columns() {
    return Number(getComputedStyle(this).getPropertyValue('--columns') || 1);
  }

  get #currentFocus() {
    if (this.#columns === 1) {
      return 'center';
    }

    return this.hasAttribute('data-move-per-page') ? undefined : 0;
  }

  get #isMobile() {
    return this.#mobileMediaQuery.matches;
  }

  get #currentType() {
    if (this.#isMobile && this.hasAttribute('data-loop-mobile')) {
      return 'loop';
    } else if (!this.#isMobile && this.hasAttribute('data-loop')) {
      return 'loop';
    }

    return 'slide';
  }
}

if (!customElements.get('grid-carousel')) {
  customElements.define('grid-carousel', GridCarousel);
}

/**
 * A custom element that manages a basic single-column carousel with Splide.js.
 *
 * @typedef {object} BasicCarouselRefs
 * @property {HTMLElement} [arrows] - The arrows container element.
 * @property {HTMLElement} [pagination] - The pagination container element.
 *
 * @extends Component<BasicCarouselRefs>
 */
class BasicCarousel extends Component {
  /** @type {MediaQueryList} */
  #mobileMediaQuery = window.matchMedia('(width < 64rem)');

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {import('@splidejs/splide').Splide} */
  #splide;

  /** @type {ResizeObserver|null} */
  #resizeObserver = null;

  /** @type {number} */
  #lastWidth = 0;

  /** @type {number|null} */
  #resizeDebounceTimer = null;

  /** @type {(() => void)|null} */
  #handleGridLayoutChange = null;

  /** @type {boolean} */
  #isProductCardCarousel = false;

  /**
   * Initializes the basic carousel when the element is connected to the DOM.
   */
  connectedCallback() {
    super.connectedCallback();

    this.#isProductCardCarousel = this.classList.contains('product-card-carousel');
    this.#initSplide();
  }

  /**
   * Initializes Splide carousel.
   */
  #initSplide() {
    if (this.#splide) return; // Already initialized

    const hasArrows = Boolean(this.refs.arrows);
    const hasPagination = Boolean(this.refs.pagination);

    const currentType = this.#currentType;
    const isFade = currentType === 'fade';
    const hasAutoplay = this.hasAttribute('data-autoplay');
    const autoplayInterval = hasAutoplay
      ? parseInt(this.getAttribute('data-autoplay-interval') || '3', 10) * 1000
      : undefined;
    const direction = this.getAttribute('data-direction') || undefined;

    const useAutoWidth = !this.#isProductCardCarousel && !isFade;

    const isVertical = direction === 'ttb';

    /** @type {string|undefined} */
    let verticalHeight = '3rem';
    if (isVertical) {
      const firstSlide = this.querySelector('.splide__slide');
      if (firstSlide) {
        verticalHeight = `${firstSlide.offsetHeight}px`;
      }
    }

    this.#splide = new Splide(
      this,
      /** @type {import('@splidejs/splide').Options} */
      {
        ...(isVertical
          ? {
              direction: 'ttb',
              height: verticalHeight,
              autoWidth: false,
              padding: 0,
              gap: 0,
              focus: 0,
            }
          : {
              autoWidth: useAutoWidth,
              padding: isFade || this.#isProductCardCarousel
                ? 0
                : {
                    left: 'var(--padding-inline-start)',
                    right: 'var(--padding-inline-end)',
                  },
              gap: isFade || this.#isProductCardCarousel ? 0 : 'var(--gap)',
              focus: this.#isProductCardCarousel ? 0 : 'center',
            }),
        arrows: hasArrows,
        perMove: 1,
        perPage: 1,
        omitEnd: !isFade && !isVertical,
        pagination: hasPagination,
        mediaQuery: 'max',
        easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
        type: currentType,
        ...(isFade ? { speed: 1000 } : {}),
        rewind: isFade,
        autoplay: hasAutoplay,
        interval: autoplayInterval,
        pauseOnHover: hasAutoplay,
        pauseOnFocus: hasAutoplay,
      },
    );

    /** @type {boolean|undefined} */
    let isOverflow = undefined;

    this.#splide.on(
      'overflow',
      function (/** @type {boolean} */ newIsOverflow) {
        if (isOverflow !== newIsOverflow) {
          isOverflow = Boolean(newIsOverflow);

          this.#splide.options = {
            drag: isOverflow,
          };
        }
      }.bind(this),
    );

    this.#mobileMediaQuery.addEventListener(
      'change',
      (e) => {
        const currentType = this.#currentType;
        const isFade = currentType === 'fade';
        const useAutoWidth = !this.#isProductCardCarousel && !isFade;

        this.#splide.options = {
          type: currentType,
          rewind: isFade,
          autoWidth: useAutoWidth,
          padding: isFade
            ? 0
            : {
                left: 'var(--padding-inline-start)',
                right: 'var(--padding-inline-end)',
              },
          gap: isFade ? 0 : 'var(--gap)',
          omitEnd: !isFade,
        };

        this.#splide.destroy();
        this.#splide.mount();
      },
      { signal: this.#abortController.signal },
    );

    this.#splide.mount();

    const lastIndex = this.#splide.length - 1;

    if (lastIndex >= 0) {
      const lastSlide = this.#splide.Components.Slides.getAt(lastIndex);
      if (lastSlide) lastSlide.slide.classList.add('is-last');
    }

    // For product card carousels, handle sizing and layout changes
    if (this.#isProductCardCarousel) {
      // Disable drag on initial load if in zoom-out mode
      if (this.#isZoomOutMode) {
        this.#splide.options = { drag: false };
        this.#splide.destroy();
        this.#splide.mount();
      }

      // Force refresh after initial layout to ensure correct slide sizing
      requestAnimationFrame(() => {
        this.#splide.refresh();
        this.#lastWidth = this.offsetWidth;
      });

      this.#setupProductCardListeners();
    }
  }

  /**
   * Sets up listeners for product card carousels (grid layout changes, resize).
   */
  #setupProductCardListeners() {
    // Listen for grid layout changes (from column picker)
    this.#handleGridLayoutChange = () => {
      this.#lastWidth = this.offsetWidth;
      const isZoomOut = this.#isZoomOutMode;

      // Toggle drag based on zoom-out mode, remount to apply
      this.#splide.options = { drag: !isZoomOut };
      this.#splide.destroy();
      this.#splide.mount();
      if (!isZoomOut) this.#splide.go(0);
    };
    document.addEventListener('grid-layout-change', this.#handleGridLayoutChange);

    // Watch for window resize (handles viewport changes)
    this.#resizeObserver = new ResizeObserver((entries) => {
      const newWidth = entries[0]?.contentRect.width ?? 0;
      if (newWidth > 0 && Math.abs(newWidth - this.#lastWidth) > 10) {
        this.#lastWidth = newWidth;
        this.#splide?.refresh();
      }
    });
    this.#resizeObserver.observe(this);
  }

  /**
   * Cleans up the basic carousel when the element is disconnected from the DOM.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#resizeDebounceTimer) {
      clearTimeout(this.#resizeDebounceTimer);
    }
    if (this.#handleGridLayoutChange) {
      document.removeEventListener('grid-layout-change', this.#handleGridLayoutChange);
    }
    this.#resizeObserver?.disconnect();
    this.#splide?.destroy(true);
  }

  /**
   * Navigates to a specific slide index.
   * @param {number} index - The slide index to navigate to.
   */
  goTo(index) {
    this.#splide?.go(index);
  }

  /**
   * Checks if the current viewport is mobile.
   * @returns {boolean} True if mobile, false if desktop.
   */
  get #isMobile() {
    return this.#mobileMediaQuery.matches;
  }

  /**
   * Checks if the grid is in zoom-out mode (small columns).
   * @returns {boolean} True if zoom-out mode, false otherwise.
   */
  get #isZoomOutMode() {
    const grid = this.closest('[product-grid-view]');
    return grid?.getAttribute('product-grid-view') === 'zoom-out';
  }

  /**
   * Determines the carousel type based on transition and loop settings.
   * @returns {string} Either 'loop', 'slide', or 'fade'.
   */
  get #currentType() {
    const transition = this.getAttribute('data-transition') || 'slide';

    if (transition === 'fade' || transition === 'crossfade') {
      return 'fade';
    }

    if (this.#isMobile && this.hasAttribute('data-loop-mobile')) {
      return 'loop';
    } else if (!this.#isMobile && this.hasAttribute('data-loop')) {
      return 'loop';
    }

    return 'slide';
  }
}

if (!customElements.get('basic-carousel')) {
  customElements.define('basic-carousel', BasicCarousel);
}
